import { useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { Banknote, CalendarClock, CalendarPlus, History, ShieldAlert, TimerReset } from 'lucide-react';
import clsx from 'clsx';

import Sheet from '@/components/Sheet';
import { apiErrorMessage, apiPost, newIdempotencyKey } from '@/lib/api';
import { addDays, addPeriod, endOfLocalDay, laterOf, toDateInputValue } from '@/lib/dates';
import { formatDate, formatDateTime, formatKop, parseRubToKop } from '@/lib/format';
import { pluralDays, type SubscriptionInfo } from '@/lib/subscription';

export interface HeadManage {
  cash: boolean;
  trial: boolean;
  days: boolean;
  blockedReason: string | null;
  suggestedKopecks: { MONTHLY: number | null; YEARLY: number | null };
  freeDaysMax: number;
  actions: Array<{
    id: string;
    action: string;
    meta: Record<string, unknown> | null;
    createdAt: string;
    manager: { id: string; name: string } | null;
  }>;
}

interface ActionResult {
  ok: true;
  subscription: SubscriptionInfo | null;
}

type SheetKind = 'cash' | 'trial' | 'days';

/** Блок «Управление сроком» в карточке заведения — только у главного менеджера. */
export function HeadActionsCard({
  establishmentId,
  subscription,
  manage,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  manage: HeadManage;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const [open, setOpen] = useState<SheetKind | null>(null);
  const close = () => setOpen(null);
  const done = (sub: SubscriptionInfo | null) => {
    setOpen(null);
    onDone(sub);
  };

  return (
    <div className="card p-4 sm:p-5 space-y-3">
      <div className="text-sm font-medium">Управление сроком</div>
      {manage.blockedReason ? (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          {manage.blockedReason}
        </div>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-3">
        <ActionButton
          icon={<Banknote size={16} />}
          label="Принять наличные"
          enabled={manage.cash}
          primary
          onClick={() => setOpen('cash')}
        />
        <ActionButton
          icon={<CalendarPlus size={16} />}
          label="Продлить триал"
          enabled={manage.trial}
          hint={manage.blockedReason ? undefined : 'Подписка оплачена — триал не действует'}
          onClick={() => setOpen('trial')}
        />
        <ActionButton
          icon={<TimerReset size={16} />}
          label="Добавить дни"
          enabled={manage.days}
          hint={manage.blockedReason ? undefined : 'Только для оплаченной подписки'}
          onClick={() => setOpen('days')}
        />
      </div>
      <p className="text-xs text-ink/45">
        Каждое действие записывается в журнал и видно администратору Resstro.
      </p>

      {open === 'cash' ? (
        <CashSheet
          establishmentId={establishmentId}
          subscription={subscription}
          suggested={manage.suggestedKopecks}
          onClose={close}
          onDone={done}
        />
      ) : null}
      {open === 'trial' ? (
        <TrialSheet
          establishmentId={establishmentId}
          subscription={subscription}
          maxDays={manage.freeDaysMax}
          onClose={close}
          onDone={done}
        />
      ) : null}
      {open === 'days' ? (
        <DaysSheet
          establishmentId={establishmentId}
          subscription={subscription}
          maxDays={manage.freeDaysMax}
          onClose={close}
          onDone={done}
        />
      ) : null}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  enabled,
  primary,
  hint,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  enabled: boolean;
  primary?: boolean;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        disabled={!enabled}
        onClick={onClick}
        className={clsx(
          primary ? 'btn-primary' : 'btn border border-line bg-white text-ink hover:bg-paper',
          'w-full disabled:cursor-not-allowed disabled:opacity-45',
        )}
      >
        {icon}
        {label}
      </button>
      {!enabled && hint ? <p className="mt-1 text-center text-[11px] text-ink/45">{hint}</p> : null}
    </div>
  );
}

// ─── Общие куски форм ─────────────────────────────────────────────────────

/** Ручка отправки: ключ идемпотентности на открытие формы и защита от двойного тапа. */
function useSubmit(establishmentId: string, path: string, onDone: (sub: SubscriptionInfo | null) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef(newIdempotencyKey());
  const inFlight = useRef(false);

  const submit = async (body: Record<string, unknown>, successText: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<ActionResult>(`/establishments/${establishmentId}/${path}`, body, {
        silent: true,
        idempotencyKey: keyRef.current,
      });
      toast.success(successText);
      onDone(res.subscription);
    } catch (err) {
      setError(apiErrorMessage(err));
      // Ответ с ошибкой не кешируется: исправленная форма уйдёт с тем же ключом.
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return { busy, error, setError, submit };
}

function Chips<T extends string | number>({
  options,
  value,
  onChange,
  format,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  format?: (v: T) => string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={String(o)}
          type="button"
          onClick={() => onChange(o)}
          className={clsx(
            'rounded-lg border px-3 py-2 text-sm font-medium min-h-[40px]',
            value === o ? 'border-orange bg-orange/10 text-ink' : 'border-line bg-white text-ink/70 hover:bg-paper',
          )}
        >
          {format ? format(o) : String(o)}
        </button>
      ))}
    </div>
  );
}

function Preview({ label, date, note }: { label: string; date: Date | null; note?: string }) {
  return (
    <div className="rounded-lg bg-paper px-3 py-2.5 text-sm">
      <span className="text-ink/55">{label} </span>
      <span className="font-semibold">{date ? formatDate(date.toISOString()) : '—'}</span>
      {note ? <div className="mt-0.5 text-xs text-ink/50">{note}</div> : null}
    </div>
  );
}

function FormError({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{text}</div>;
}

function parseDays(raw: string, max: number): number | null {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= max ? n : null;
}

// ─── Принять наличные ─────────────────────────────────────────────────────

type CashMode = 'MONTHLY' | 'YEARLY' | 'days' | 'until';

const CASH_MODES: Array<{ value: CashMode; label: string }> = [
  { value: 'MONTHLY', label: 'Месяц' },
  { value: 'YEARLY', label: 'Год' },
  { value: 'days', label: 'N дней' },
  { value: 'until', label: 'До даты' },
];

function CashSheet({
  establishmentId,
  subscription,
  suggested,
  onClose,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  suggested: HeadManage['suggestedKopecks'];
  onClose: () => void;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const now = new Date();
  // Как activatePaidPeriod: от конца живого оплаченного периода, иначе от сегодня.
  const periodEnd = subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const base = laterOf(now, periodEnd);

  const initialKop = suggested.MONTHLY ?? subscription?.amountKopecks ?? null;
  const [mode, setMode] = useState<CashMode>('MONTHLY');
  const [days, setDays] = useState('30');
  const [until, setUntil] = useState(() => toDateInputValue(addDays(base, 30)));
  const [amount, setAmount] = useState(initialKop != null ? String(initialKop / 100) : '');
  const [amountTouched, setAmountTouched] = useState(false);
  const [note, setNote] = useState('');
  const { busy, error, setError, submit } = useSubmit(establishmentId, 'cash-payment', onDone);

  const pickMode = (m: CashMode) => {
    setMode(m);
    // Сумму подставляем по тарифу, пока её не правили руками.
    if (!amountTouched && (m === 'MONTHLY' || m === 'YEARLY') && suggested[m] != null) {
      setAmount(String(suggested[m]! / 100));
    }
  };

  const daysNum = parseDays(days, 1100);
  const untilDate = endOfLocalDay(until);
  const resultEnd =
    mode === 'MONTHLY' || mode === 'YEARLY'
      ? addPeriod(base, mode)
      : mode === 'days'
        ? daysNum
          ? addDays(base, daysNum)
          : null
        : untilDate;
  const amountKop = parseRubToKop(amount);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (amountKop == null || amountKop < 100) return setError('Сумма — от 1 ₽');
    const body: Record<string, unknown> = { amountKopecks: amountKop };
    if (mode === 'MONTHLY' || mode === 'YEARLY') body.period = mode;
    else if (mode === 'days') {
      if (!daysNum) return setError('Дней — от 1 до 1100');
      body.days = daysNum;
    } else {
      if (!untilDate || untilDate.getTime() <= base.getTime()) {
        return setError('Дата должна быть позже текущего оплаченного периода');
      }
      body.until = untilDate.toISOString();
    }
    if (note.trim()) body.note = note.trim();
    void submit(body, `Оплата ${formatKop(amountKop)} принята`);
  };

  return (
    <Sheet title="Принять наличные" onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <div className="label">Оплачиваемый срок</div>
          <Chips
            options={CASH_MODES.map((m) => m.value)}
            value={mode}
            onChange={pickMode}
            format={(v) => CASH_MODES.find((m) => m.value === v)!.label}
          />
        </div>

        {mode === 'days' ? (
          <div>
            <label className="label" htmlFor="cash-days">Дней</label>
            <input
              id="cash-days"
              className="input"
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        ) : null}
        {mode === 'until' ? (
          <div>
            <label className="label" htmlFor="cash-until">Оплачено до</label>
            <input
              id="cash-until"
              className="input"
              type="date"
              min={toDateInputValue(addDays(base, 1))}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        ) : null}

        <div>
          <label className="label" htmlFor="cash-amount">Принято, ₽</label>
          <input
            id="cash-amount"
            className="input"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountTouched(true);
            }}
          />
          <p className="mt-1 text-xs text-ink/45">
            Подставлена цена тарифа с модулями — впишите сумму, которую фактически взяли.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="cash-note">Заметка</label>
          <input
            id="cash-note"
            className="input"
            maxLength={300}
            placeholder="чек, кто передал…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <Preview
          label="Оплачено до"
          date={resultEnd}
          note={periodEnd && periodEnd > now ? `Продлевается от ${formatDate(periodEnd.toISOString())}` : 'Срок считается от сегодня'}
        />
        <FormError text={error} />

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          <Banknote size={16} />
          {busy ? 'Проводим…' : amountKop && amountKop >= 100 ? `Принять ${formatKop(amountKop)}` : 'Принять'}
        </button>
      </form>
    </Sheet>
  );
}

// ─── Продлить триал ───────────────────────────────────────────────────────

const TRIAL_PRESETS = [7, 14, 30] as const;

function TrialSheet({
  establishmentId,
  subscription,
  maxDays,
  onClose,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  maxDays: number;
  onClose: () => void;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const now = new Date();
  // Как adjustTrial: от конца триала, если он ещё впереди, иначе от сегодня.
  const trialEnd = subscription?.trialEndsAt ? new Date(subscription.trialEndsAt) : null;
  const base = laterOf(now, trialEnd);

  const [mode, setMode] = useState<'days' | 'until'>('days');
  const [days, setDays] = useState('7');
  const [until, setUntil] = useState(() => toDateInputValue(addDays(base, 7)));
  const { busy, error, setError, submit } = useSubmit(establishmentId, 'trial', onDone);

  const daysNum = parseDays(days, maxDays);
  const untilDate = endOfLocalDay(until);
  const resultEnd = mode === 'days' ? (daysNum ? addDays(base, daysNum) : null) : untilDate;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'days') {
      if (!daysNum) return setError(`Дней — от 1 до ${maxDays}`);
      void submit({ days: daysNum }, `Триал продлён на ${daysNum} ${pluralDays(daysNum)}`);
      return;
    }
    if (!untilDate || untilDate.getTime() <= base.getTime()) {
      return setError('Дата должна быть позже текущего окончания триала');
    }
    void submit({ until: untilDate.toISOString() }, 'Триал продлён');
  };

  return (
    <Sheet title="Продлить триал" onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <Chips
          options={['days', 'until'] as const}
          value={mode}
          onChange={setMode}
          format={(v) => (v === 'days' ? 'На N дней' : 'До даты')}
        />

        {mode === 'days' ? (
          <div className="space-y-2">
            <label className="label" htmlFor="trial-days">Дней (до {maxDays})</label>
            <input
              id="trial-days"
              className="input"
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
            />
            <Chips
              options={TRIAL_PRESETS.filter((n) => n <= maxDays)}
              value={Number(days) as (typeof TRIAL_PRESETS)[number]}
              onChange={(n) => setDays(String(n))}
              format={(n) => `+${n}`}
            />
          </div>
        ) : (
          <div>
            <label className="label" htmlFor="trial-until">Триал до</label>
            <input
              id="trial-until"
              className="input"
              type="date"
              min={toDateInputValue(addDays(base, 1))}
              max={toDateInputValue(addDays(base, maxDays))}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        )}

        <Preview
          label="Триал до"
          date={resultEnd}
          note={trialEnd && trialEnd > now ? `Сейчас до ${formatDate(trialEnd.toISOString())}` : 'Триал уже закончился — считаем от сегодня'}
        />
        <FormError text={error} />

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          <CalendarPlus size={16} />
          {busy ? 'Продлеваем…' : 'Продлить триал'}
        </button>
      </form>
    </Sheet>
  );
}

// ─── Добавить дни без оплаты ──────────────────────────────────────────────

const DAYS_PRESETS = [3, 7, 14, 30] as const;

function DaysSheet({
  establishmentId,
  subscription,
  maxDays,
  onClose,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  maxDays: number;
  onClose: () => void;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const now = new Date();
  const periodEnd = subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const base = laterOf(now, periodEnd);

  const [days, setDays] = useState('7');
  const [note, setNote] = useState('');
  const { busy, error, setError, submit } = useSubmit(establishmentId, 'days', onDone);

  const daysNum = parseDays(days, maxDays);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!daysNum) return setError(`Дней — от 1 до ${maxDays}`);
    const body: Record<string, unknown> = { days: daysNum };
    if (note.trim()) body.note = note.trim();
    void submit(body, `Добавлено ${daysNum} ${pluralDays(daysNum)}`);
  };

  return (
    <Sheet title="Добавить дни" onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <p className="text-sm text-ink/60">
          Оплаченный период продлится без платежа — для компенсации простоя или договорённости
          с клиентом.
        </p>
        <div className="space-y-2">
          <label className="label" htmlFor="days-count">Дней (до {maxDays})</label>
          <input
            id="days-count"
            className="input"
            inputMode="numeric"
            value={days}
            onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
          />
          <Chips
            options={DAYS_PRESETS.filter((n) => n <= maxDays)}
            value={Number(days) as (typeof DAYS_PRESETS)[number]}
            onChange={(n) => setDays(String(n))}
            format={(n) => `+${n}`}
          />
        </div>
        <div>
          <label className="label" htmlFor="days-note">Причина</label>
          <input
            id="days-note"
            className="input"
            maxLength={300}
            placeholder="простой кассы 2 дня, договорились…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <Preview label="Оплачено до" date={daysNum ? addDays(base, daysNum) : null} note="Без платежа и без выручки" />
        <FormError text={error} />

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          <TimerReset size={16} />
          {busy ? 'Добавляем…' : 'Добавить дни'}
        </button>
      </form>
    </Sheet>
  );
}

// ─── Разовые дни пробного (обычный менеджер) ──────────────────────────────

export interface TrialGraceInfo {
  available: boolean;
  /** ok — можно; used — уже дали; running — пробный идёт; paid / blocked / none — не про пробный. */
  code: 'ok' | 'used' | 'running' | 'paid' | 'blocked' | 'none';
  reason: string | null;
  usedAt: string | null;
  maxDays: number;
}

/** «до 1 дня», «до 3 дней» — после «до» родительный падеж. */
function upToDays(n: number): string {
  return `до ${n} ${n % 10 === 1 && n % 100 !== 11 ? 'дня' : 'дней'}`;
}

/** Показываем, только когда это про пробный: платящему клиенту блок ни к чему. */
export function showTrialGrace(info: TrialGraceInfo | null | undefined): info is TrialGraceInfo {
  return !!info && (info.code === 'ok' || info.code === 'used' || info.code === 'running');
}

export function TrialGraceCard({
  establishmentId,
  subscription,
  info,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  info: TrialGraceInfo;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card space-y-3 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700">
          <CalendarClock size={17} />
        </span>
        <div className="min-w-0 text-sm">
          <div className="font-medium">Пробный период</div>
          <p className="mt-0.5 text-ink/60">
            {info.available
              ? `Клиент не успел разобраться за пробный — можно один раз дать ещё ${upToDays(info.maxDays)}.`
              : info.code === 'running'
                ? `${info.reason}. Если клиент не успеет разобраться, после окончания можно один раз дать ещё ${upToDays(info.maxDays)}.`
                : info.reason}
          </p>
        </div>
      </div>
      {info.available ? (
        <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setOpen(true)}>
          <CalendarPlus size={16} /> Дать {upToDays(info.maxDays)} пробного
        </button>
      ) : null}
      {open ? (
        <TrialGraceSheet
          establishmentId={establishmentId}
          subscription={subscription}
          maxDays={info.maxDays}
          onClose={() => setOpen(false)}
          onDone={(sub) => {
            setOpen(false);
            onDone(sub);
          }}
        />
      ) : null}
    </div>
  );
}

function TrialGraceSheet({
  establishmentId,
  subscription,
  maxDays,
  onClose,
  onDone,
}: {
  establishmentId: string;
  subscription: SubscriptionInfo | null;
  maxDays: number;
  onClose: () => void;
  onDone: (sub: SubscriptionInfo | null) => void;
}) {
  const presets = Array.from({ length: maxDays }, (_, i) => i + 1);
  const [days, setDays] = useState(maxDays);
  const { busy, error, submit } = useSubmit(establishmentId, 'trial-grace', onDone);
  // Пробный уже кончился — сервер считает от сейчас.
  const resultEnd = addDays(new Date(), days);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit({ days }, `Пробный продлён на ${days} ${pluralDays(days)}`);
  };

  return (
    <Sheet title="Дать дни пробного" onClose={onClose}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <div className="label">Сколько дней</div>
          <Chips options={presets} value={days} onChange={setDays} format={(n) => `+${n}`} />
        </div>
        <Preview
          label="Пробный до"
          date={resultEnd}
          note={
            subscription?.accessEndsAt
              ? `Закончился ${formatDate(subscription.accessEndsAt)} — считаем от сегодня`
              : 'Считаем от сегодня'
          }
        />
        <p className="text-xs text-ink/50">
          Один раз на заведение. Дальше продлевают главный менеджер или руководитель.
        </p>
        <FormError text={error} />
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          <CalendarPlus size={16} />
          {busy ? 'Продлеваем…' : `Дать ${days} ${pluralDays(days)}`}
        </button>
      </form>
    </Sheet>
  );
}

// ─── Журнал действий по заведению ─────────────────────────────────────────

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const PROFILE_FIELD_LABEL: Record<string, string> = {
  name: 'название',
  address: 'адрес',
  phone: 'телефон',
  ownerName: 'имя владельца',
  ownerPhone: 'телефон владельца',
};

/** Человеческая строка журнала: что сделали и до какого числа теперь доступ. */
export function describeAction(action: string, meta: Record<string, unknown> | null): string {
  const m = meta ?? {};
  const after = str(m.after);
  const until = after ? ` · до ${formatDate(after)}` : '';
  const days = num(m.days);
  switch (action) {
    case 'establishment.cash_payment': {
      const amount = num(m.amountKopecks);
      return `Наличные ${amount != null ? formatKop(amount) : ''}${until}`.trim();
    }
    case 'establishment.trial_extend':
      return `Триал продлён${days ? ` на ${days} ${pluralDays(days)}` : ''}${until}`;
    case 'establishment.days_add':
      return `+${days ?? '?'} ${pluralDays(days ?? 0)} без оплаты${until}`;
    case 'establishment.trial_grace':
      return `Менеджер дал ${days ?? '?'} ${pluralDays(days ?? 0)} пробного${until}`;
    case 'establishment.update': {
      const changes = m.changes && typeof m.changes === 'object' ? Object.keys(m.changes) : [];
      const labels = changes.map((k) => PROFILE_FIELD_LABEL[k] ?? k);
      return labels.length ? `Профиль изменён: ${labels.join(', ')}` : 'Профиль изменён';
    }
    case 'establishment.referrer': {
      const to = (m.to as { name?: unknown } | null | undefined)?.name;
      const from = (m.from as { name?: unknown } | null | undefined)?.name;
      if (typeof to === 'string') return typeof from === 'string' ? `Менеджер: ${from} → ${to}` : `Назначен менеджер ${to}`;
      return typeof from === 'string' ? `Снята привязка к ${from}` : 'Привязка снята';
    }
    default:
      return action;
  }
}

export function HeadJournal({ actions }: { actions: HeadManage['actions'] }) {
  if (!actions.length) return null;
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3 text-sm font-medium">
        <History size={15} className="text-ink/40" />
        Действия по заведению
      </div>
      <ul className="divide-y divide-line/60">
        {actions.map((a) => {
          const note = str(a.meta?.note);
          return (
            <li key={a.id} className="px-4 py-3">
              <div className="text-sm font-medium">{describeAction(a.action, a.meta)}</div>
              <div className="mt-0.5 text-xs text-ink/50">
                {a.manager?.name ?? 'менеджер удалён'} · {formatDateTime(a.createdAt)}
                {note ? ` · ${note}` : ''}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
